// game/shared/src/doodad-placement.ts
//
// Shared, deterministic placement resolver: nudges decorative ROCKS out of the
// things they should never visually clip — harvestable nodes, trees, and other
// rocks — by RELOCATING them a few metres, never deleting. Pure + deterministic,
// so the client render (world.ts), the server collision (static-obstacles.ts),
// and the navmesh bake (navmesh.ts) all see the same resolved positions.
//
// WHY THIS EXISTS. KTS authored objects.json for its small original SC2 props.
// Our re-themed EXPLORER meshes are far wider per instance (a tree's visual
// footprint is ~6× its collision trunk), so placements that were fine at the old
// scale now interpenetrate — most visibly a boulder poking through a tree. The
// earlier dedupeDoodads pass tried to fix this by DELETING overlappers, which
// thinned the map ~50% and still missed the harvestable cases. This resolver
// keeps every doodad (density intact) and instead moves the offending rocks to
// the nearest clear spot.
//
// SCOPE (v1). Movers = decorative rocks only. Anchors (never moved) = harvestable
// nodes + decorative trees + already-placed rocks. This removes rock-in-tree,
// rock-on-rock, and rock-on-harvestable — the bulk of the visible overlap —
// without touching forest canopy density (tree-on-tree overlap is a forest) or
// the small walk-through clutter (plants/bones). Stone-deposit-under-decorative-
// tree (a harvestable rock under a tree) is out of scope here: we don't move
// harvestables, and moving the tree risks cascading the dense canopy.
//
// FOOTPRINTS. Radii come from the real baked mesh bounding boxes (MESH_RADIUS,
// regenerated from the GLBs — see tools/asset-export) × the render scale × a
// per-category factor. Trees use a BASE factor (trunk/low-canopy), not the full
// canopy radius: a rock just outside the trunk but under high foliage reads fine,
// and full-canopy clearance is both unnecessary and often unresolvable in dense
// forest. Rocks use a near-full factor (they're solid ground mass).

import { DOODAD_REGISTRY } from "./doodad-registry";
import { CLIFF_PLATEAU_LEVELS } from "./cliff-edges";

// Horizontal visual radius (world metres) of each baked nature mesh = max(bboxX,
// bboxZ)/2. Generated from the GLBs in client/public/assets/nature via
// tools/asset-export. Regenerate when the asset pack changes. Quaternius meshes
// not in this table fall back to DEFAULT_MESH_R.
export const MESH_RADIUS: Record<string, number> = {
  exBone1A: 0.48, exBone2A: 0.1, exCanyonW1A: 12.17, exCanyonW2A: 11.81, exCanyonW3A: 11.8,
  exCarcass1A: 1.46, exCarcass2A: 1.91, exCliff1A: 1.55, exCliff2A: 1.68, exCliff5A: 8.93,
  exFern1A: 1.23, exFern2A: 1.39, exLog1A: 1.05, exLog2A: 1.91,
  exMush1A: 0.21, exMush2A: 0.08, exMush3A: 0.2, exMush4A: 0.12, exMush5A: 0.6,
  exRockL1A: 1.55, exRockL1B: 1.71, exRockL2A: 2.2, exRockL3A: 2.04,
  exRockM1A: 1.02, exRockM1B: 1.02, exRockM2A: 1.0, exRockM2B: 1.0, exRockM3A: 1.1, exRockM3C: 0.94,
  exRockS1A: 0.48, exRockS1B: 0.64, exRockS1C: 0.34, exRockS1D: 0.57,
  exTree1A: 3.22, exTree1B: 3.22, exTree1E: 1.84, exTree2B: 3.08, exTree2D: 3.09, exTree2F: 1.85,
  exTree3A: 1.84, exTree3C: 1.79, exTree3E: 1.0, exTree4B: 6.88, exTree4C: 7.34,
  exTree5A: 4.66, exTree5B: 6.88, exTree5C: 7.48, exTree6B: 5.36, exTree6C: 5.07,
  exTree7A: 2.2, exTree7B: 5.05, exTree7C: 5.33, exTree8A: 7.97, exTree8B: 6.93,
  exTree8C: 14.42, exTree8D: 11.83, exTree8E: 6.0, exTrunk1A: 0.73, exTusk1A: 0.46,
};
const DEFAULT_MESH_R = 0.5; // Quaternius bush/flower/pebble meshes (not baked)
const meshR = (key: string): number => MESH_RADIUS[key] ?? DEFAULT_MESH_R;

// Render scale the client applies to an UNREGISTERED doodad, by shape bucket —
// mirrors FALLBACK_RENDER_SCALE in server/src/static-obstacles.ts and the
// client's FALLBACK_BUCKETS. Used only for the <5% of types not in the registry.
const FALLBACK_RENDER_SCALE = { tree: 1.14, rock: 3.0, other: 1.09 };

// Per-category footprint factors (× mesh radius × render scale). Trees: base/
// trunk footprint, not canopy. Rocks: near-full (solid mass).
const TREE_FACTOR = 0.4;
const ROCK_FACTOR = 0.85;
const BERRY_FACTOR = 0.6;

// Harvestable resource nodes (kind:"unit") that act as immovable anchors. Mesh +
// baseScale mirror client/src/resources.ts HARVESTABLE_KINDS; radius uses the
// LARGEST mesh in each pool so clearance holds whichever variant renders.
const HARVESTABLE_ANCHORS: Record<string, { meshR: number; factor: number }> = {
  MapleTree:        { meshR: Math.max(meshR("exTree1A"), meshR("exTree1B"), meshR("exTree1E")) * 0.70, factor: TREE_FACTOR },
  ElderTree:        { meshR: meshR("exTree8D") * 0.60, factor: TREE_FACTOR },
  StoneDeposit:     { meshR: Math.max(meshR("exRockM1A"), meshR("exRockM2A"), meshR("exRockM3A")) * 0.80, factor: ROCK_FACTOR },
  GoldDeposit:      { meshR: Math.max(meshR("exRockM1A"), meshR("exRockM2A"), meshR("exRockM3A")) * 0.80, factor: ROCK_FACTOR },
  BerryBush:        { meshR: DEFAULT_MESH_R * 0.63, factor: BERRY_FACTOR },
  CulinaryHerbBush: { meshR: DEFAULT_MESH_R * 0.63, factor: BERRY_FACTOR },
};

export type PlacementRow = {
  kind: "unit" | "doodad";
  type: string;
  x: number;
  y: number; // KTS world-Z plane coordinate (see objects.json export)
  scale?: number;
  // …other fields (z, rot, variation) pass through untouched.
};

const isTree = (type: string): boolean => /Tree$/i.test(type);
const isRock = (type: string): boolean => /Rock|Rubble|Stone/i.test(type);

// Largest visual footprint a decorative type can render at (max over its variant
// pool), × the per-instance object scale × the category factor. Position-
// independent, so the moved rock clears no matter which variant render hashes to.
const typeFootprintCache = new Map<string, number>();
export function decorFootprint(type: string, objScale: number): number {
  let base = typeFootprintCache.get(type);
  if (base === undefined) {
    const row = DOODAD_REGISTRY[type];
    const factor = isTree(type) ? TREE_FACTOR : ROCK_FACTOR;
    if (row && row !== "skip") {
      const maxMesh = Math.max(...row.variants.map(meshR));
      base = maxMesh * row.scale * factor;
    } else {
      const rs = isTree(type) ? FALLBACK_RENDER_SCALE.tree : FALLBACK_RENDER_SCALE.rock;
      base = DEFAULT_MESH_R * rs * factor;
    }
    typeFootprintCache.set(type, base);
  }
  return base * (objScale > 0 ? objScale : 1);
}

function anchorRadius(o: PlacementRow): number | null {
  if (o.kind === "unit") {
    const h = HARVESTABLE_ANCHORS[o.type];
    return h ? h.meshR * h.factor * (o.scale && o.scale > 0 ? o.scale : 1) : null;
  }
  if (o.kind === "doodad" && isTree(o.type)) return decorFootprint(o.type, o.scale ?? 1);
  return null; // plants/bones — not anchors
}

type Occ = { x: number; z: number; r: number };

// ── Narrow-ramp corridor anchors (2026-07-05, map-flip follow-up) ─────────────
// Solid rocks must never collision-block a NARROW ramp corridor (the SouthRezz-
// Shrine west mouth was pinched shut by two rocks sitting on its ramp cells).
// We seed every narrow-corridor cell (+1-cell mouth dilation) as an immovable
// anchor so the resolver's existing nudge machinery pushes rocks out — density
// preserved, and render/collision/navmesh all consume the same resolved rows.
// "Narrow" = the cell's local ramp run is ≤ NARROW_RUN_MAX cells in x or z:
// authored connector ramps qualify; wide hillside slope fields don't (a rock on
// a broad slope is walk-aroundable and should stay). MAX_MOVE additionally
// caps relocation, so even a mis-classified slope cell can't fling rocks.
const NARROW_RUN_MAX = 4;
const RAMP_CLEAR_R = 1.2; // cell-cover (~0.71) + corridor clearance margin
// A plateau component this small is a "narrow hill" (the SouthRezzShrine top is
// ~70 cells): its whole surface is a transit lane between its ramps, so solid
// rocks anywhere on it can seal the crossing. Big highlands (thousands of
// cells) are untouched.
const SMALL_PLATEAU_MAX_CELLS = 140;
const isRampLevel = (l: number): boolean => l !== 0 && !CLIFF_PLATEAU_LEVELS.has(l);

/** Anchor circles covering narrow ramp corridors (+1-cell mouth dilation) and
 *  the full surface of SMALL plateau components ("narrow hills"). */
export function rampCorridorAnchors(cliffLevels: Uint16Array, cellDim = 256): Occ[] {
  const lvl = (c: number, r: number): number =>
    c >= 0 && c < cellDim && r >= 0 && r < cellDim ? cliffLevels[r * cellDim + c]! : 0;
  const ramp = (c: number, r: number): boolean => isRampLevel(lvl(c, r)) && lvl(c, r) !== 0;
  const narrow = new Uint8Array(cellDim * cellDim);
  for (let r = 0; r < cellDim; r++) {
    for (let c = 0; c < cellDim; c++) {
      if (!ramp(c, r)) continue;
      let runX = 1;
      for (let i = c - 1; ramp(i, r); i--) runX++;
      for (let i = c + 1; ramp(i, r); i++) runX++;
      let runZ = 1;
      for (let i = r - 1; ramp(c, i); i--) runZ++;
      for (let i = r + 1; ramp(c, i); i++) runZ++;
      if (Math.min(runX, runZ) <= NARROW_RUN_MAX) narrow[r * cellDim + c] = 1;
    }
  }
  // Small-plateau components: flood-fill 4-connected same-level plateau cells;
  // components ≤ SMALL_PLATEAU_MAX_CELLS join the band wholesale.
  const seen = new Uint8Array(cellDim * cellDim);
  for (let r = 0; r < cellDim; r++) {
    for (let c = 0; c < cellDim; c++) {
      const i0 = r * cellDim + c;
      const l0 = cliffLevels[i0]!;
      if (seen[i0] || l0 === 0 || !CLIFF_PLATEAU_LEVELS.has(l0)) continue;
      const comp: number[] = [];
      const stack = [i0];
      seen[i0] = 1;
      while (stack.length) {
        const i = stack.pop()!;
        comp.push(i);
        const cc = i % cellDim, rr = (i / cellDim) | 0;
        for (const [dc, dr] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
          const nc = cc + dc, nr = rr + dr;
          if (nc < 0 || nc >= cellDim || nr < 0 || nr >= cellDim) continue;
          const ni = nr * cellDim + nc;
          if (!seen[ni] && cliffLevels[ni] === l0) { seen[ni] = 1; stack.push(ni); }
        }
      }
      if (comp.length <= SMALL_PLATEAU_MAX_CELLS) for (const i of comp) narrow[i] = 1;
    }
  }
  const band = new Uint8Array(cellDim * cellDim);
  for (let r = 0; r < cellDim; r++)
    for (let c = 0; c < cellDim; c++)
      if (narrow[r * cellDim + c])
        for (let dr = -1; dr <= 1; dr++)
          for (let dc = -1; dc <= 1; dc++) {
            const rr = r + dr, cc = c + dc;
            if (rr >= 0 && rr < cellDim && cc >= 0 && cc < cellDim) band[rr * cellDim + cc] = 1;
          }
  const out: Occ[] = [];
  for (let r = 0; r < cellDim; r++)
    for (let c = 0; c < cellDim; c++)
      if (band[r * cellDim + c]) out.push({ x: c + 0.5, z: r + 0.5, r: RAMP_CLEAR_R });
  return out;
}

const CELL = 12; // > max(anchorR)+max(moverR); 3×3 neighbourhood can't miss a hit
const MARGIN = 1;
const MAX_ITERS = 16;
const EPS = 0.02;
// Hard cap on how far a rock may travel from its authored spot. In dense forest a
// rock can't always find clear ground; rather than fling it across the map (a
// 30m relocation is worse than the overlap), we clamp it to this disc and accept
// whatever residual overlap remains at the boundary — a few metres reads as a
// natural reposition, not a teleport.
const MAX_MOVE = 6;

/**
 * Relocate decorative rocks out of harvestable nodes, trees, each other — and
 * (when `cliffLevels` is provided) out of NARROW ramp corridors, so a solid rock
 * can never collision-block a connector ramp. Pure + deterministic: walks rocks
 * in input order, returns a NEW array with the moved rocks' x/y updated (height
 * re-derives from terrain at render). All other rows pass through by reference.
 * `mapUnits` clamps moves to the playable square. ALL production call sites
 * (client render, server collision, navmesh bake) must pass the same
 * `cliffLevels` or rendered/collided/pathed diverge.
 */
export function resolveDoodadPlacement<T extends PlacementRow>(
  rows: T[],
  mapUnits = 256,
  cliffLevels?: Uint16Array,
): T[] {
  const grid = new Map<string, Occ[]>();
  const key = (cx: number, cz: number): string => `${cx},${cz}`;
  const add = (o: Occ): void => {
    const k = key(Math.floor(o.x / CELL), Math.floor(o.z / CELL));
    let b = grid.get(k);
    if (!b) { b = []; grid.set(k, b); }
    b.push(o);
  };
  const neighbours = (x: number, z: number): Occ[] => {
    const out: Occ[] = [];
    const cx = Math.floor(x / CELL), cz = Math.floor(z / CELL);
    for (let dx = -1; dx <= 1; dx++)
      for (let dz = -1; dz <= 1; dz++) {
        const b = grid.get(key(cx + dx, cz + dz));
        if (b) out.push(...b);
      }
    return out;
  };

  // 1. Seed all immovable anchors (harvestables + decorative trees), plus the
  //    narrow-ramp corridor band when cliff data is supplied.
  for (const o of rows) {
    const r = anchorRadius(o);
    if (r !== null) add({ x: o.x, z: o.y, r });
  }
  if (cliffLevels) for (const a of rampCorridorAnchors(cliffLevels, mapUnits)) add(a);

  // 2. Walk decorative rocks in stable order; nudge each clear, then add it as an
  //    occupant so later rocks avoid it too.
  const lo = MARGIN, hi = mapUnits - MARGIN;
  const clamp = (v: number): number => (v < lo ? lo : v > hi ? hi : v);
  const out: T[] = [];
  for (let i = 0; i < rows.length; i++) {
    const o = rows[i]!;
    if (!(o.kind === "doodad" && isRock(o.type))) { out.push(o); continue; }
    const r = decorFootprint(o.type, o.scale ?? 1);
    const ox = o.x, oz = o.y;
    let x = ox, z = oz;
    for (let iter = 0; iter < MAX_ITERS; iter++) {
      let pushX = 0, pushZ = 0, hits = 0;
      for (const occ of neighbours(x, z)) {
        const ddx = x - occ.x, ddz = z - occ.z;
        const reach = r + occ.r;
        const d2 = ddx * ddx + ddz * ddz;
        if (d2 >= reach * reach) continue;
        hits++;
        const d = Math.sqrt(d2);
        const depth = reach - d + EPS;
        if (d > 1e-4) { pushX += (ddx / d) * depth; pushZ += (ddz / d) * depth; }
        else {
          // Coincident: deterministic direction from indices (no RNG).
          const a = ((i * 2 + occ.r) % 6.2831853);
          pushX += Math.cos(a) * depth; pushZ += Math.sin(a) * depth;
        }
      }
      if (hits === 0) break;
      x = clamp(x + pushX);
      z = clamp(z + pushZ);
      // Keep the rock within MAX_MOVE of its authored spot — no teleporting.
      const mdx = x - ox, mdz = z - oz;
      const md = Math.sqrt(mdx * mdx + mdz * mdz);
      if (md > MAX_MOVE) { x = ox + (mdx / md) * MAX_MOVE; z = oz + (mdz / md) * MAX_MOVE; }
    }
    add({ x, z, r });
    out.push(x === o.x && z === o.y ? o : ({ ...o, x, y: z } as T));
  }
  return out;
}
